import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown } from "lucide-react";

export type SelectOption<T extends string = string> = {
  value: T;
  label: string;
  description?: string;
  disabled?: boolean;
};

type CompactSelectProps<T extends string> = {
  ariaLabel: string;
  value: T;
  options: readonly SelectOption<T>[];
  onChange: (value: T) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  buttonClassName?: string;
};

type ListPosition = {
  left: number;
  top: number;
  width: number;
  maxHeight: number;
};

export function CompactSelect<T extends string>({
  ariaLabel,
  value,
  options,
  onChange,
  disabled = false,
  placeholder = "Select",
  className = "",
  buttonClassName = "",
}: CompactSelectProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<ListPosition | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const listId = useId();
  const selected = options.find((option) => option.value === value);

  const close = useCallback((restoreFocus = false) => {
    setIsOpen(false);
    setPosition(null);
    if (restoreFocus) window.setTimeout(() => buttonRef.current?.focus(), 0);
  }, []);

  const updatePosition = useCallback(() => {
    const trigger = buttonRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const gutter = 8;
    const width = Math.min(
      Math.max(rect.width, 190),
      Math.max(190, window.innerWidth - gutter * 2)
    );
    const estimatedHeight = Math.min(280, options.length * 48 + 8);
    const dialog = trigger.closest<HTMLElement>("[role='dialog']");
    const actionFooter = dialog?.querySelector<HTMLElement>(
      ".orbit-form-mobile-footer, .orbit-filter-actions"
    );
    const footerTop = actionFooter?.getBoundingClientRect().top;
    const bottomBoundary = Math.min(
      window.innerHeight - gutter,
      footerTop ? footerTop - gutter : window.innerHeight - gutter
    );
    const availableBelow = Math.max(0, bottomBoundary - rect.bottom - 4);
    const availableAbove = Math.max(0, rect.top - gutter);
    const fitsBelow = availableBelow >= Math.min(estimatedHeight, 120);
    const maxHeight = Math.max(
      96,
      Math.min(estimatedHeight, fitsBelow ? availableBelow : availableAbove)
    );
    const top = fitsBelow
      ? rect.bottom + 4
      : Math.max(gutter, rect.top - maxHeight - 4);
    const left = Math.min(
      Math.max(gutter, rect.left),
      window.innerWidth - width - gutter
    );
    setPosition({ left, top, width, maxHeight });
  }, [options.length]);

  const open = useCallback(() => {
    if (disabled) return;
    setIsOpen(true);
  }, [disabled]);

  useEffect(() => {
    if (!isOpen) return;
    updatePosition();
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (
        !buttonRef.current?.contains(target) &&
        !listRef.current?.contains(target)
      ) {
        close();
      }
    };
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [close, isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen || !position) return;
    const selectedIndex = Math.max(
      0,
      options.findIndex((option) => option.value === value && !option.disabled)
    );
    window.setTimeout(() => optionRefs.current[selectedIndex]?.focus(), 0);
  }, [isOpen, options, position, value]);

  const moveFocus = (direction: 1 | -1) => {
    const enabledIndexes = options
      .map((option, index) => (!option.disabled ? index : -1))
      .filter((index) => index >= 0);
    if (enabledIndexes.length === 0) return;
    const currentIndex = optionRefs.current.findIndex(
      (option) => option === document.activeElement
    );
    const enabledPosition = enabledIndexes.indexOf(currentIndex);
    const nextPosition =
      enabledPosition < 0
        ? 0
        : (enabledPosition + direction + enabledIndexes.length) %
          enabledIndexes.length;
    optionRefs.current[enabledIndexes[nextPosition]]?.focus();
  };

  const handleListKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      close(true);
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      moveFocus(1);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      moveFocus(-1);
    } else if (event.key === "Home") {
      event.preventDefault();
      optionRefs.current.find((option) => option && !option.disabled)?.focus();
    } else if (event.key === "End") {
      event.preventDefault();
      [...optionRefs.current]
        .reverse()
        .find((option) => option && !option.disabled)
        ?.focus();
    } else if (event.key === "Tab") {
      close();
    }
  };

  return (
    <div className={`relative min-w-0 ${className}`}>
      <button
        ref={buttonRef}
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={isOpen ? listId : undefined}
        disabled={disabled}
        onClick={() => (isOpen ? close() : open())}
        onKeyDown={(event) => {
          if (["ArrowDown", "ArrowUp", "Enter", " "].includes(event.key)) {
            event.preventDefault();
            open();
          }
        }}
        className={`orbit-select-trigger inline-flex min-h-11 w-full min-w-0 items-center justify-between gap-2 rounded-xl border px-3 py-2 text-left text-sm font-semibold outline-none disabled:cursor-not-allowed disabled:opacity-45 ${buttonClassName}`}
      >
        <span className="truncate">{selected?.label ?? placeholder}</span>
        <ChevronDown
          size={16}
          className={`shrink-0 transition ${isOpen ? "rotate-180" : ""}`}
        />
      </button>
      {isOpen && position
        ? createPortal(
            <div
              ref={listRef}
              id={listId}
              role="listbox"
              aria-label={ariaLabel}
              onKeyDown={handleListKeyDown}
              className="orbit-select-list fixed z-[100] max-h-[280px] overflow-y-auto rounded-xl p-1.5 shadow-2xl"
              style={position}
            >
              {options.map((option, index) => {
                const isSelected = option.value === value;
                return (
                  <button
                    key={option.value}
                    ref={(element) => {
                      optionRefs.current[index] = element;
                    }}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    disabled={option.disabled}
                    onClick={() => {
                      onChange(option.value);
                      close(true);
                    }}
                    className={`flex min-h-11 w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition disabled:opacity-40 ${
                      isSelected ? "orbit-select-option-active" : "orbit-select-option"
                    }`}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-semibold">{option.label}</span>
                      {option.description ? (
                        <span className="mt-0.5 block text-xs text-slate-500">
                          {option.description}
                        </span>
                      ) : null}
                    </span>
                    {isSelected ? <Check size={16} className="shrink-0" /> : null}
                  </button>
                );
              })}
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
